import { useState, type CSSProperties } from 'react';

import { cn } from '@/shared/lib/utils';
import { DropdownMenu } from '@/shared/ui/dropdown-menu';
import { Icon } from '@/shared/ui/icon';

import { PHX_FEELS, PHX_SCHEMES, type PhxFeelMeta, type PhxSchemeMeta } from './phxTheme';
import { usePhxTheme } from './usePhxTheme';

export interface SchemeSwitcherProps {
  className?: string;
  /** Alignment of the dropdown relative to its trigger. Defaults to `end`. */
  align?: 'start' | 'end';
}

/** Vertical (bottom-up) axis caption — matches the design-system reference switcher. */
const AXIS_LABEL =
  'text-phx-3xs font-bold uppercase tracking-[0.08em] text-phx-text-muted select-none [writing-mode:vertical-rl] rotate-180';

/**
 * Phoenix scheme & feel picker — a compact dropdown whose trigger shows the
 * active scheme (round swatch in the theme colour) and feel (square swatch in
 * the surface tint).  The menu lays the two axes side by side, exactly like
 * the design-system reference switcher: 18 round scheme dots next to 12
 * square feel swatches, each grid with its own vertical caption.  Picking a
 * swatch keeps the menu open so several combinations can be tried in a row.
 * Reads & writes the `data-phx-scheme` / `data-phx-feel` attributes via
 * `usePhxTheme`, persisting the selection in `localStorage`.
 */
export function SchemeSwitcher({ className, align = 'end' }: SchemeSwitcherProps) {
  const { mode, scheme, feel, setScheme, setFeel } = usePhxTheme();
  const [open, setOpen] = useState(false);

  const swatchOf = (s: PhxSchemeMeta) => (mode === 'dark' ? s.darkColor : s.color);
  // Hard 50/50 diagonal split between the family's two preview tints (the
  // reference switcher's swatch style, saturated for legibility at 14px).
  const feelSwatchOf = (f: PhxFeelMeta) => {
    const [c1, c2] = mode === 'dark' ? [f.darkColor, f.darkColor2] : [f.color, f.color2];
    return `linear-gradient(135deg, ${c1} 0%, ${c1} 50%, ${c2} 50%, ${c2} 100%)`;
  };
  const activeScheme = PHX_SCHEMES.find((s) => s.id === scheme) ?? PHX_SCHEMES[0];
  const activeFeel = PHX_FEELS.find((f) => f.id === feel) ?? PHX_FEELS[0];

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      align={align}
      trigger={
        <button
          type="button"
          title={`Theme — ${activeScheme.label} · ${activeFeel.label}`}
          aria-label={`Theme — ${activeScheme.label} · ${activeFeel.label}`}
          className={cn(
            'phx-scheme-switcher gap-phx-2 rounded-phx-sm px-phx-2 inline-flex h-6 cursor-pointer items-center border-0 bg-transparent',
            'text-phx-text-muted hover:text-phx-text hover:bg-phx-surface-2 transition-colors',
            className
          )}
        >
          <span
            aria-hidden
            className="phx-theme-swatch h-3.5 w-3.5 rounded-full"
            style={{
              background: swatchOf(activeScheme),
              boxShadow: '0 0 0 1px var(--phx-border-3)',
            }}
          />
          <span
            aria-hidden
            className="phx-theme-swatch rounded-phx-xs h-3.5 w-3.5"
            style={{
              background: feelSwatchOf(activeFeel),
              boxShadow: '0 0 0 1px var(--phx-border-3)',
            }}
          />
          <Icon name="chevdown" size={12} className="opacity-70" />
        </button>
      }
    >
      <div className="px-phx-3 py-phx-2 gap-phx-5 flex items-center">
        {/* THEME — 18 colored dots in rows of 6 */}
        <div className="gap-phx-3 flex items-center">
          <span aria-hidden className={AXIS_LABEL}>
            Theme
          </span>
          <div className="grid grid-cols-6 gap-0.75">
            {PHX_SCHEMES.map((s) => {
              const isActive = scheme === s.id;
              const swatch = swatchOf(s);
              const swatchStyle: CSSProperties = {
                background: swatch,
                boxShadow: isActive
                  ? `0 0 0 1.5px var(--phx-surface), 0 0 0 3px ${swatch}, 0 0 0 4px var(--phx-border-3)`
                  : '0 0 0 1px var(--phx-border-3)',
              };
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setScheme(s.id)}
                  title={s.label}
                  aria-label={`Scheme ${s.label}`}
                  aria-pressed={isActive}
                  className="rounded-phx-sm hover:bg-phx-surface-2 inline-flex h-5 w-5 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                >
                  <span
                    aria-hidden
                    data-active={isActive ? '' : undefined}
                    className="phx-theme-swatch h-3 w-3 rounded-full transition-shadow"
                    style={swatchStyle}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <span aria-hidden className="bg-phx-border h-5.5 w-px shrink-0" />

        {/* FEEL — 12 surface-family swatches in rows of 6 */}
        <div className="gap-phx-3 flex items-center">
          <span aria-hidden className={AXIS_LABEL}>
            Feel
          </span>
          <div className="grid grid-cols-6 gap-0.75">
            {PHX_FEELS.map((f) => {
              const isActive = feel === f.id;
              const ringColor = mode === 'dark' ? f.darkColor2 : f.color2;
              const swatchStyle: CSSProperties = {
                background: feelSwatchOf(f),
                boxShadow: isActive
                  ? `0 0 0 1.5px var(--phx-surface), 0 0 0 3px ${ringColor}, 0 0 0 4px var(--phx-border-3)`
                  : '0 0 0 1px var(--phx-border-3)',
              };
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFeel(f.id)}
                  title={f.label}
                  aria-label={`Feel ${f.label}`}
                  aria-pressed={isActive}
                  className="rounded-phx-sm hover:bg-phx-surface-2 inline-flex h-5.5 w-5.5 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                >
                  <span
                    aria-hidden
                    data-active={isActive ? '' : undefined}
                    className="phx-theme-swatch rounded-phx h-3.5 w-3.5 transition-shadow"
                    style={swatchStyle}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </DropdownMenu>
  );
}
